import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import Icon from '../../elements/Icon';
import Box from '../../elements/Box';
import Text from '../../elements/Text';
import Button from '../../elements/Button';
import { getUuid } from '@front10/helpers/dist/uuid';

const Alert = (props) => {
  const [isAlertVisible, setIsAlertVisible] = useState(true);
  const timerToDisappear = React.useRef(null);
  const timerFadeOut = React.useRef(null);
  const id = getUuid();
  const {
    labelKey,
    buttonLabelKey,
    showCloseButton,
    bgColor,
    textColor,
    focusReferences,
    iconSettings,
    timeToDissapear,
    onCloseAction,
    containerSettings,
    enableFocusedBox = true
  } = props;
  const fadeOut = !isAlertVisible ? 'fadeOut' : '';
  const { translate, direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });

  const handleVisibility = React.useCallback(() => {
    setIsAlertVisible(false);
    timerFadeOut.current = setTimeout(() => {
      onCloseAction();
    }, 500);
  }, [onCloseAction]);

  useEffect(() => {
    if (timeToDissapear > 0) {
      timerToDisappear.current = setTimeout(() => {
        handleVisibility();
      }, timeToDissapear * 1000);
    }

    return () => {
      if (timerToDisappear) {
        clearTimeout(timerToDisappear);
        timerToDisappear.current = null;
      }
      if (timerFadeOut) {
        clearTimeout(timerFadeOut);
        timerFadeOut.current = null;
      }
    };
  }, [handleVisibility, timeToDissapear]);

  return (
    <Box
      display="flex"
      alignItems="center"
      rtlReverse
      className={`${className} ${fadeOut} fr-alert`}
      style={style}
      role="alert"
      focusReferences={focusReferences}
      tabIndex={enableFocusedBox ? 0 : null}
      {...bgColor} //TODO: Deprecate props for next release
      {...textColor} //TODO: Deprecate props for next release
      {...containerSettings}
    >
      <Text id={`descriptionText-${id}`} className="fr-sr-only" ariaHidden={true}>
        {`${translate(
          labelKey,
          'Oh snap! It was an issue pulling your awesome deals :('
        )} ${translate(buttonLabelKey, 'Close alert')} `}
      </Text>
      <div
        data-testid="alertDiv"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: translate(labelKey, 'Oh snap! It was an issue pulling your awesome deals :(')
        }}
      />
      {showCloseButton && (
        <Button
          textColor={textColor}
          ariaLabelledBy={`descriptionText-${id}`}
          color="link"
          dataTestId="alertBTN"
          onClick={handleVisibility}
        >
          <Icon color={textColor} {...iconSettings} icon="close" />
        </Button>
      )}
    </Box>
  );
};

Alert.propTypes = {
  /**
   * Class name of header
   */
  className: PropTypes.string,
  /**
   * Css style applied to the component
   */
  style: PropTypes.objectOf(PropTypes.any),
  /**
   * Alert's message key
   */
  labelKey: PropTypes.string,
  /**
   * Alert's message labelKey
   */
  buttonLabelKey: PropTypes.string,
  /**
   * Time to hide the Alert
   */
  timeToDissapear: PropTypes.number,
  /**
   * showCloseButton
   */
  showCloseButton: PropTypes.bool,
  /**
   * Function fired when click
   */
  onCloseAction: PropTypes.func,
  /**
   * Defaults bgColor
   */
  bgColor: PropTypes.string,
  /**
   * Defaults textColor
   */
  textColor: PropTypes.string,
  /**
   * focus references
   */
  focusReferences: PropTypes.func,
  /**
   * icon props
   */
  iconSettings: PropTypes.objectOf(PropTypes.any)
};

Alert.defaultProps = {
  className: '',
  style: null,
  labelKey: 'em-cmp-alert',
  buttonLabelKey: 'cmp-alert-button-name',
  timeToDissapear: 0,
  showCloseButton: true,
  onCloseAction: () => {},
  iconSettings: {},
  bgColor: 'secondary-4',
  textColor: 'black',
  focusReferences: () => {}
};

Alert.componentName = 'Alert';

export default Alert;
